import { useFormContext } from 'react-hook-form';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import cns from 'classnames';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useViewportDevice } from '@zep/react';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogOverlay, DialogPanel, DialogTitle } from '@zep/ui';
import { useRegisterPaymentCard } from '../../../../../services';
import useCopyPrevention from '../../hooks/useCopyPrevention';
import { PaymentForm } from '../PaymentForm';
import { PaymentFormProvider } from '../PaymentFormProvider';
import { RegisterPaymentSchemaType } from '../schema/PaymentCardSchema';
import S from './PaymentCardModal.module.scss';
export const Modal = (props: RegisterPaymentCardModalProps) => {
  const {
    visible,
    remove,
    hide
  } = useModal();
  const {
    t
  } = useTranslation(['subscription']);
  return <Dialog maxWidth="640px" open={visible} onClose={() => null} afterClose={remove} drawer data-sentry-element="Dialog" data-sentry-component="Modal" data-sentry-source-file="PaymentCardModal.tsx">
      <DialogOverlay data-sentry-element="DialogOverlay" data-sentry-source-file="PaymentCardModal.tsx" />
      <PaymentFormProvider data-sentry-element="PaymentFormProvider" data-sentry-source-file="PaymentCardModal.tsx">
        <DialogPanel data-sentry-element="DialogPanel" data-sentry-source-file="PaymentCardModal.tsx">
          <DialogHeader onClose={hide} data-sentry-element="DialogHeader" data-sentry-source-file="PaymentCardModal.tsx">
            <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="PaymentCardModal.tsx">
              {t('subscription:SpaceDetail.menuManage.billingCardChangeButton')}
            </DialogTitle>
          </DialogHeader>
          <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="PaymentCardModal.tsx">
            <Content data-sentry-element="Content" data-sentry-source-file="PaymentCardModal.tsx" />
          </DialogContent>
          <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="PaymentCardModal.tsx">
            <Action {...props} data-sentry-element="Action" data-sentry-source-file="PaymentCardModal.tsx" />
          </DialogFooter>
        </DialogPanel>
      </PaymentFormProvider>
    </Dialog>;
};
const Content = () => {
  useCopyPrevention();
  const {
    isMobile
  } = useViewportDevice();
  const {
    watch
  } = useFormContext<RegisterPaymentSchemaType>();
  const isInc = watch('isBusiness');
  return <div className={S.content} data-sentry-component="Content" data-sentry-source-file="PaymentCardModal.tsx">
      {!isMobile && <div className={S.left}>
          <Image src="/images/checkout/credit-card.png" alt="" width={150} height={150} />
        </div>}
      <div className={cns(S.right, S.field_group)}>
        <PaymentForm.InputCardNumber size={'md'} data-sentry-element="unknown" data-sentry-source-file="PaymentCardModal.tsx" />
        <PaymentForm.InputExpireDate size={'md'} data-sentry-element="unknown" data-sentry-source-file="PaymentCardModal.tsx" />
        {isInc ? <PaymentForm.InputIncNumber size={'md'} /> : <PaymentForm.InputBirthDate size={'md'} />}
        <PaymentForm.InputCardName size={'md'} data-sentry-element="unknown" data-sentry-source-file="PaymentCardModal.tsx" />
      </div>
    </div>;
};
const Action = ({
  spaceHashId,
  paymentFlow,
  onSuccess,
  onError
}: RegisterPaymentCardModalProps) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    mutateAsync
  } = useRegisterPaymentCard();
  const {
    getValues,
    handleSubmit,
    formState: {
      isDirty,
      errors
    }
  } = useFormContext<RegisterPaymentSchemaType>();
  const isError = Object.keys(errors).length > 0;
  const onSubmit = async () => {
    const {
      isBusiness,
      businessNumber,
      cardNumber,
      expireDate,
      birthDate,
      name: cardName
    } = getValues();
    try {
      await mutateAsync({
        spaceHashId,
        paymentFlow,
        cardExpirationMonth: expireDate.substring(0, 2),
        cardExpirationYear: expireDate.substring(2, 4),
        customerIdentityNumber: isBusiness ? businessNumber : birthDate,
        cardNumber,
        cardName
      });
    } catch (e) {
      await onError(e);
      return;
    }
    await onSuccess();
  };
  return <div data-sentry-component="Action" data-sentry-source-file="PaymentCardModal.tsx">
      <div className={S.button_area}>
        <div className={S.col}>
          <PaymentForm.IncCheckBox data-sentry-element="unknown" data-sentry-source-file="PaymentCardModal.tsx" />
        </div>
        <div className={S.col}>
          <Button type="button" onClick={handleSubmit(onSubmit)} disabled={isError || !isDirty} isFullWidth data-sentry-element="Button" data-sentry-source-file="PaymentCardModal.tsx">
            {t('subscription:payment.cardRegister.complete')}
          </Button>
        </div>
      </div>
    </div>;
};
const _RegisterPaymentCardModal = (props: RegisterPaymentCardModalProps) => {
  return <PaymentFormProvider data-sentry-element="PaymentFormProvider" data-sentry-component="_RegisterPaymentCardModal" data-sentry-source-file="PaymentCardModal.tsx">
      <Modal {...props} data-sentry-element="Modal" data-sentry-source-file="PaymentCardModal.tsx" />
    </PaymentFormProvider>;
};
export const RegisterPaymentCardModal = NiceModal.create(_RegisterPaymentCardModal);
export type RegisterPaymentCardModalProps = {
  spaceHashId: string;
  paymentFlow?: 'card_registration' | 'card_change';
  onSuccess: () => void | Promise<void>;
  onError: (error: unknown) => void | Promise<void>;
};